/* @img-migrated */
import React, { useState, useEffect } from 'react';
import { Link, Outlet, useLocation, useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Home, MessageSquare, Bell, User, Tag, Shield, Plus, X, Download
} from 'lucide-react';
import { useAuth } from '../../context/AuthContext';
import axios from 'axios';

import Img from '../Img';
import { resolveImg } from '../../lib/images';
const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const ADMIN_ROLES = ['super_admin', 'membership_lead', 'membership_support', 'marketing_team', 'events_manager', 'blog_reviewer'];

const tabs = [
  { key: 'feed', path: '/app/feed', icon: Home, label: 'Feed' },
  { key: 'messages', path: '/app/messages', icon: MessageSquare, label: 'Messages' },
  { key: 'notifications', path: '/app/notifications', icon: Bell, label: 'Alerts' },
  { key: 'deals', path: '/app/deals', icon: Tag, label: 'Deals' },
  { key: 'profile', path: '/app/profile', icon: User, label: 'Profile' },
];

const adminTab = { key: 'admin', path: '/app/admin', icon: Shield, label: 'Admin' };

const MobileAppLayout = () => {
  const { user, loading } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [unreadMessages, setUnreadMessages] = useState(0);
  const [unreadNotifs, setUnreadNotifs] = useState(0);

  // Auth gate: anything under /app/* requires login
  useEffect(() => {
    if (!loading && !user) {
      const next = encodeURIComponent(location.pathname + location.search);
      navigate(`/member-login?next=${next}`, { replace: true });
    }
  }, [user, loading, navigate, location]);

  // Lightweight counters for the tab-bar dots
  useEffect(() => {
    if (!user) return;
    const fetchCounts = async () => {
      try {
        const [m, n] = await Promise.all([
          axios.get(`${API}/portal/messages/unread-count`, { withCredentials: true }).catch(() => null),
          axios.get(`${API}/notifications?limit=1`, { withCredentials: true }).catch(() => null),
        ]);
        if (m?.data) setUnreadMessages(m.data.count ?? m.data.unread_count ?? 0);
        if (n?.data) setUnreadNotifs(n.data.unread_count || 0);
      } catch { /* silent */ }
    };
    fetchCounts();
    const t = setInterval(fetchCounts, 45000);
    return () => clearInterval(t);
  }, [user]);

  const isAdmin = ADMIN_ROLES.includes(user?.role);
  const activeTabs = isAdmin ? [...tabs, adminTab] : tabs;
  const isActive = (path) => location.pathname === path || location.pathname.startsWith(path + '/');
  const showFAB = location.pathname === '/app/feed';

  if (loading || !user) {
    return (
      <div className="dccf-app-shell flex items-center justify-center bg-[#001028] text-white">
        <div className="w-8 h-8 border-2 border-white/20 border-t-white rounded-full animate-spin" />
      </div>
    );
  }

  // Page meta from path
  const currentPage = activeTabs.find((t) => isActive(t.path));
  const headerTitle = currentPage?.label || 'DCCF';

  return (
    <div className="dccf-app-shell bg-slate-50 min-h-[100dvh] flex flex-col" data-testid="mobile-app-shell">
      {/* TOP HEADER */}
      <header className="dccf-app-header sticky top-0 z-30 bg-white border-b border-slate-200">
        <div className="h-14 flex items-center justify-between px-4">
          <Link to="/app/feed" className="flex items-center gap-2" data-testid="app-logo">
            <div className="w-8 h-8 rounded-lg bg-[#001028] flex items-center justify-center">
              <span className="text-white font-bold text-[12px] tracking-wider">DC</span>
            </div>
            <span className="text-[15px] font-bold text-[#002249] tracking-tight">{headerTitle}</span>
          </Link>
          <div className="flex items-center gap-1">
            <Link
              to="/app/notifications"
              className={`relative h-10 w-10 rounded-full flex items-center justify-center ${isActive('/app/notifications') ? 'text-[#002249]' : 'text-slate-500'} hover:bg-slate-100 transition-colors`}
              data-testid="header-notifications"
            >
              <Bell className="w-5 h-5" />
              {unreadNotifs > 0 && (
                <span className="absolute top-1.5 right-1.5 min-w-[16px] h-4 px-1 rounded-full bg-[#E63946] text-white text-[10px] font-bold flex items-center justify-center">
                  {unreadNotifs > 9 ? '9+' : unreadNotifs}
                </span>
              )}
            </Link>
            <Link to="/app/profile" className="h-10 w-10 rounded-full overflow-hidden flex items-center justify-center" data-testid="header-profile">
              {user?.picture ? (
                <Img variant="avatar" src={resolveImg(user.picture)} alt="" className="w-9 h-9 rounded-full object-cover ring-2 ring-white shadow-sm" />
              ) : (
                <div className="w-9 h-9 rounded-full bg-gradient-to-br from-[#002249] to-[#0a3570] flex items-center justify-center text-white font-bold text-[12px] shadow-sm">
                  {user?.name?.charAt(0).toUpperCase()}
                </div>
              )}
            </Link>
          </div>
        </div>
      </header>

      {/* CONTENT */}
      <main className="dccf-app-main flex-1 min-h-0 overflow-y-auto pb-[calc(76px+env(safe-area-inset-bottom))]" data-testid="mobile-app-main">
        <Outlet context={{ unreadMessages, unreadNotifs, isAdmin, user }} />
      </main>

      {/* FAB — Compose post on Feed */}
      <AnimatePresence>
        {showFAB && (
          <motion.button
            initial={{ scale: 0, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            exit={{ scale: 0, opacity: 0 }}
            whileTap={{ scale: 0.92 }}
            onClick={() => window.dispatchEvent(new CustomEvent('dccf:open-composer'))}
            className="dccf-fab absolute z-40 h-14 w-14 rounded-full bg-[#E63946] text-white flex items-center justify-center shadow-[0_8px_20px_-6px_rgba(230,57,70,0.55)]"
            style={{ bottom: 'calc(86px + env(safe-area-inset-bottom))', right: '20px' }}
            data-testid="app-fab-compose"
            aria-label="New post"
          >
            <Plus className="w-6 h-6" />
          </motion.button>
        )}
      </AnimatePresence>

      {/* BOTTOM NAV */}
      <nav
        className="dccf-app-tabbar absolute bottom-0 left-0 right-0 z-30 bg-white border-t border-slate-200"
        style={{ paddingBottom: 'env(safe-area-inset-bottom)' }}
        data-testid="mobile-app-tabbar"
      >
        <div className="grid h-[64px]" style={{ gridTemplateColumns: `repeat(${activeTabs.length}, 1fr)` }}>
          {activeTabs.map((t) => {
            const active = isActive(t.path);
            const badge =
              t.key === 'messages' ? unreadMessages :
              t.key === 'notifications' ? unreadNotifs : 0;
            return (
              <Link
                key={t.key}
                to={t.path}
                className={`relative flex flex-col items-center justify-center gap-0.5 transition-colors ${active ? 'text-[#002249]' : 'text-slate-400 hover:text-slate-600'}`}
                data-testid={`tab-${t.key}`}
              >
                <div className="relative">
                  <t.icon className={`w-[22px] h-[22px] transition-transform ${active ? 'scale-110' : ''}`} strokeWidth={active ? 2.4 : 1.8} />
                  {badge > 0 && (
                    <span className="absolute -top-1.5 -right-2 min-w-[16px] h-4 px-1 rounded-full bg-[#E63946] text-white text-[9px] font-bold flex items-center justify-center">
                      {badge > 9 ? '9+' : badge}
                    </span>
                  )}
                </div>
                <span className={`text-[10.5px] tracking-wide ${active ? 'font-semibold' : 'font-medium'}`}>{t.label}</span>
                {active && (
                  <motion.span
                    layoutId="tab-indicator"
                    className="absolute top-0 h-[3px] w-10 rounded-b-full bg-[#E63946]"
                    transition={{ type: 'spring', stiffness: 360, damping: 30 }}
                  />
                )}
              </Link>
            );
          })}
        </div>
      </nav>

      {/* INSTALL PROMPT */}
      <PWAInstallBanner />
    </div>
  );
};

/* ─── PWA Install Banner (Android Chrome / Edge) ─────────────────────────── */
const PWAInstallBanner = () => {
  const [deferred, setDeferred] = useState(null);
  const [hidden, setHidden] = useState(() => {
    try { return localStorage.getItem('dccf_pwa_dismissed') === '1'; } catch { return false; }
  });
  const [installed, setInstalled] = useState(false);

  useEffect(() => {
    const handler = (e) => {
      e.preventDefault();
      setDeferred(e);
    };
    const installedHandler = () => setInstalled(true);
    window.addEventListener('beforeinstallprompt', handler);
    window.addEventListener('appinstalled', installedHandler);
    // Already running as standalone (installed)
    if (window.matchMedia?.('(display-mode: standalone)').matches) setInstalled(true);
    return () => {
      window.removeEventListener('beforeinstallprompt', handler);
      window.removeEventListener('appinstalled', installedHandler);
    };
  }, []);

  const install = async () => {
    if (!deferred) return;
    deferred.prompt();
    await deferred.userChoice;
    setDeferred(null);
  };
  const dismiss = () => {
    setHidden(true);
    try { localStorage.setItem('dccf_pwa_dismissed', '1'); } catch { /* */ }
  };

  if (installed || hidden || !deferred) return null;

  return (
    <motion.div
      initial={{ y: 24, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      exit={{ y: 24, opacity: 0 }}
      className="absolute left-3 right-3 z-40 bg-[#002249] text-white rounded-2xl shadow-2xl px-4 py-3 flex items-center gap-3"
      style={{ bottom: 'calc(82px + env(safe-area-inset-bottom))' }}
      data-testid="pwa-install-banner"
    >
      <div className="w-10 h-10 rounded-xl bg-white/10 flex items-center justify-center flex-shrink-0">
        <Download className="w-5 h-5 text-amber-300" />
      </div>
      <div className="min-w-0 flex-1">
        <p className="text-[13px] font-bold leading-tight">Install DCCF Network</p>
        <p className="text-[11.5px] text-white/65 mt-0.5">Faster access, always one tap away.</p>
      </div>
      <button onClick={install} className="h-9 px-3 rounded-lg bg-[#E63946] hover:bg-[#c02a35] text-white text-[12.5px] font-bold transition-colors" data-testid="pwa-install-btn">
        Install
      </button>
      <button onClick={dismiss} className="text-white/60 hover:text-white p-1" aria-label="Dismiss">
        <X className="w-4 h-4" />
      </button>
    </motion.div>
  );
};

export default MobileAppLayout;
